import { useEffect } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar } from '@mui/material';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';

import LAYOUT_CONST from '@/constant';
import useConfig from '@/hooks/useConfig';
import { drawerWidth } from '@/store/constant';
import { setCurrentHospitalId } from '@/store/slices/hospital';
import { openDrawer } from '@/store/slices/menu';
import { useAppDispatch, useAppSelector } from '@/hooks';

type TMainStyleProps = {
    open: boolean;
};

// styles
const Main = styled('main', { shouldForwardProp: (isOpen) => isOpen !== 'open' })(
    ({ theme: { breakpoints, transitions, typography }, open }: { theme: Theme } & TMainStyleProps) => ({
        ...typography.mainContent,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        // height: 'calc(100vh - 88px)',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        marginTop: 88,
        width: `calc(100% - ${drawerWidth}px)`,
        ...(open
            ? {
                  transition: transitions.create('margin', {
                      easing: transitions.easing.easeOut,
                      duration: transitions.duration.shorter
                  }),
                  marginLeft: 0,
                  [breakpoints.down('md')]: {
                      marginLeft: '20px'
                  },
                  [breakpoints.down('sm')]: {
                      marginLeft: '10px'
                  }
              }
            : {
                  transition: transitions.create('margin', {
                      easing: transitions.easing.sharp,
                      duration: transitions.duration.shorter
                  }),
                  [breakpoints.up('md')]: {
                      marginLeft: -(drawerWidth - 72)
                  },
                  [breakpoints.down('md')]: {
                      marginLeft: '20px',
                      padding: '16px 16px 0'
                  },
                  [breakpoints.down('sm')]: {
                      marginLeft: '10px',
                      padding: '16px 16px 0',
                      marginRight: '10px'
                  }
              })
    })
);

const HeaderWithToolbar = (
    <Toolbar sx={{ p: '16px' }}>
        <Header />
    </Toolbar>
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const { palette } = useTheme();
    const { pathname } = useLocation();
    const dispatch = useAppDispatch();
    const { drawerOpen } = useAppSelector(({ menu }) => menu);
    const { currentHospitalId } = useAppSelector(({ hospital }) => hospital);
    const { drawerType, container } = useConfig();
    const hospitalRouteMatch = matchPath('/admin/hospitals/:hospitalId/:detailTab/*', pathname);
    const { hospitalId } = hospitalRouteMatch?.params || {};

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
    }, []);

    useEffect(() => {
        if (!hospitalId || hospitalId === currentHospitalId) {
            return;
        }

        dispatch(setCurrentHospitalId(hospitalId));
    }, [currentHospitalId, dispatch, hospitalId]);

    return (
        <Box sx={{ display: 'flex', overflow: 'hidden', height: '100vh' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{ bgcolor: palette.background.default, zIndex: 1100 }}
            >
                {HeaderWithToolbar}
            </AppBar>
            {/* drawer */}
            <Sidebar />
            {/* main content */}
            <Main open={drawerOpen}>
                <Container
                    maxWidth={container ? 'lg' : false}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        width: '100%',
                        ...(!container && { px: { xs: 0 } })
                    }}
                >
                    <Outlet />
                </Container>
                <Footer />
            </Main>
        </Box>
    );
};

export default MainLayout;
